// src/utils/sounds.js
// Programmatic sound engine using Web Audio API.
// No external audio files — all sounds generated in real time.
// Designed to be subtle and premium, like Little Snitch.

let _ctx = null
let _enabled = true

function getCtx() {
  if (!_ctx) _ctx = new (window.AudioContext || window.webkitAudioContext)()
  // Resume audio context if suspended (browser security requirement)
  if (_ctx.state === 'suspended') {
    _ctx.resume()
  }
  return _ctx
}

export function setSoundEnabled(val) {
  _enabled = val
  localStorage.setItem('netwoke-sounds', val ? '1' : '0')
}

export function isSoundEnabled() {
  const stored = localStorage.getItem('netwoke-sounds')
  if (stored === null) return true // on by default
  return stored === '1'
}

// Initialize enabled state from storage
_enabled = isSoundEnabled()

// ─── Core tone generator ──────────────────────────────────────────────────────
function tone({ freq = 440, type = 'sine', gain = 0.08, duration = 0.12, attack = 0.01, decay = 0.06, delay = 0 } = {}) {
  if (!_enabled) return
  try {
    const ctx = getCtx()
    
    // Resume audio context if suspended (browser requirement)
    if (ctx.state === 'suspended') {
      ctx.resume()
    }
    
    const osc = ctx.createOscillator()
    const env = ctx.createGain()

    osc.connect(env)
    env.connect(ctx.destination)

    osc.type = type
    osc.frequency.setValueAtTime(freq, ctx.currentTime + delay)

    env.gain.setValueAtTime(0, ctx.currentTime + delay)
    env.gain.linearRampToValueAtTime(gain, ctx.currentTime + delay + attack)
    env.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + attack + decay)

    osc.start(ctx.currentTime + delay)
    osc.stop(ctx.currentTime + delay + duration)
  } catch (_) {}
}

// ─── Individual sounds ────────────────────────────────────────────────────────

// New connection detected — soft sonar ping
export function playConnectionSound() {
  tone({ freq: 880,  type: 'sine',     gain: 0.04, attack: 0.005, decay: 0.15, duration: 0.18 })
  tone({ freq: 1320, type: 'sine',     gain: 0.02, attack: 0.005, decay: 0.10, duration: 0.12, delay: 0.04 })
}

// Alert fired — slightly more noticeable double tone
export function playAlertSound() {
  tone({ freq: 660,  type: 'triangle', gain: 0.07, attack: 0.01,  decay: 0.12, duration: 0.15 })
  tone({ freq: 880,  type: 'triangle', gain: 0.05, attack: 0.01,  decay: 0.10, duration: 0.12, delay: 0.12 })
}

// Critical alert — more urgent triple tone
export function playCriticalSound() {
  tone({ freq: 440,  type: 'triangle', gain: 0.09, attack: 0.005, decay: 0.10, duration: 0.12 })
  tone({ freq: 550,  type: 'triangle', gain: 0.07, attack: 0.005, decay: 0.10, duration: 0.12, delay: 0.13 })
  tone({ freq: 660,  type: 'triangle', gain: 0.09, attack: 0.005, decay: 0.15, duration: 0.18, delay: 0.26 })
}

// Kill process success — satisfying power-down whoosh
export function playKillSound() {
  if (!_enabled) return
  try {
    const ctx = getCtx()
    
    // Resume audio context if suspended (browser requirement)
    if (ctx.state === 'suspended') {
      ctx.resume()
    }
    
    const osc = ctx.createOscillator()
    const env = ctx.createGain()

    osc.connect(env)
    env.connect(ctx.destination)

    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(440, ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.3)

    env.gain.setValueAtTime(0.07, ctx.currentTime)
    env.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3)

    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.35)
  } catch (_) {}
}

// Block IP success — short sharp lock click
export function playBlockSound() {
  if (!_enabled) return
  try {
    const ctx = getCtx()
    
    // Resume audio context if suspended (browser requirement)
    if (ctx.state === 'suspended') {
      ctx.resume()
    }
  } catch (_) {}
  
  tone({ freq: 1200, type: 'square',   gain: 0.04, attack: 0.001, decay: 0.05, duration: 0.07 })
  tone({ freq: 800,  type: 'square',   gain: 0.03, attack: 0.001, decay: 0.04, duration: 0.06, delay: 0.06 })
}

// Typing sound for AI explanation - soft mechanical click
export function playTypingSound() {
  if (!_enabled) return
  try {
    const ctx = getCtx()
    
    const bufferSize = ctx.sampleRate * 0.06 // 60ms — slightly longer for more presence
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
    const data = buffer.getChannelData(0)

    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 12) // Less aggressive decay
    }

    const source = ctx.createBufferSource()
    source.buffer = buffer

    const highpass = ctx.createBiquadFilter()
    highpass.type = 'highpass'
    highpass.frequency.value = 800 // Lower frequency for more presence

    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0.15, ctx.currentTime) // Increased volume
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06)

    source.connect(highpass)
    highpass.connect(gain)
    gain.connect(ctx.destination)

    source.start()
    source.stop(ctx.currentTime + 0.06)
  } catch (_) {}
}

// Hover sound for UI interaction - subtle sine chirp
export function playHoverSound() {
  if (!_enabled) return
  try {
    const ctx = getCtx()
    
    // Resume audio context if suspended (browser requirement)
    if (ctx.state === 'suspended') {
      ctx.resume()
    }

    // Soft sine tone — very short, very quiet, like a subtle UI ping
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = 'sine'
    osc.frequency.setValueAtTime(880, ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(1100, ctx.currentTime + 0.04)

    gain.gain.setValueAtTime(0.04, ctx.currentTime) // Very quiet
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08)

    osc.connect(gain)
    gain.connect(ctx.destination)

    osc.start()
    osc.stop(ctx.currentTime + 0.08)
  } catch (_) {}
}

// App launch — gentle startup chime
export function playStartupSound() {
  tone({ freq: 523,  type: 'sine',     gain: 0.05, attack: 0.02,  decay: 0.2,  duration: 0.25, delay: 0.1 })
  tone({ freq: 659,  type: 'sine',     gain: 0.04, attack: 0.02,  decay: 0.2,  duration: 0.25, delay: 0.25 })
  tone({ freq: 784,  type: 'sine',     gain: 0.05, attack: 0.02,  decay: 0.3,  duration: 0.35, delay: 0.4 })
}
